import { CartLinesUpdateEvent, ProductSelectEvent } from '@shopify/events';

/**
 * Émet l'event Web Pixels `product_added_to_cart` pour chaque item ajouté.
 * Consommé par Shopify Analytics + custom pixels (Meta, GA4, TikTok via App Pixels).
 * Payload conforme au schéma standard Shopify Analytics.
 */
function publishAddedToCart(items) {
  if (!items || !Array.isArray(items)) return;
  const publish = window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish;
  if (typeof publish !== 'function') return;
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
  items.forEach((item) => {
    try {
      publish.call(window.Shopify.analytics, 'product_added_to_cart', {
        cartLine: {
          cost: { totalAmount: { amount: (item.line_price || item.final_line_price || 0) / 100, currencyCode: currency } },
          merchandise: {
            id: 'gid://shopify/ProductVariant/' + (item.variant_id || item.id),
            price: { amount: (item.price || item.final_price || 0) / 100, currencyCode: currency },
            product: { id: 'gid://shopify/Product/' + (item.product_id || 0), title: item.product_title || item.title || '', vendor: item.vendor || '', untranslatedTitle: item.product_title || '' },
            sku: item.sku || '',
            title: item.variant_title || item.title || '',
            untranslatedTitle: item.variant_title || item.title || '',
          },
          quantity: item.quantity || 1,
        },
      });
    } catch (e) { /* ne bloque jamais l'ajout */ }
  });
}

/**
 * <bundle-buy-box> — buy box complète (réplique du mockup Trooss).
 *
 * Sélecteur Matière/Couleur du haut = trousse unique (carte "1 Trooss") + pilote la galerie.
 * Cartes "2 Trooss" / "3 Trooss" = choix par trousse (segmenté matière + pastilles couleur),
 * affiché uniquement quand la carte est sélectionnée.
 *
 * Backed by real product variants (JSON injecté par le Liquid). La remise "2 achetées = 3ᵉ
 * offerte" est une remise automatique Shopify : ce composant estime le prix avant ajout et
 * laisse le panier réel refléter le montant exact facturé.
 */
class BundleBuyBox extends HTMLElement {
  connectedCallback() {
    const jsonEl = this.querySelector('script[data-bundle-json]');
    if (!jsonEl) return;

    this.data = JSON.parse(jsonEl.textContent);
    this.swatchFallback = this.data.swatchFallback || {};
    this.productUrl = this.dataset.productUrl;
    this.productId = this.dataset.productId;
    this.productTitle = this.dataset.productTitle;
    this.productHandle = this.dataset.productHandle;
    this.sectionId = this.dataset.sectionId;
    this.colorOptionName = this.dataset.colorOptionName;
    this.colorPosition = Number(this.dataset.colorPosition);
    this.materialOptionName = this.dataset.materialOptionName;
    this.materialPosition = Number(this.dataset.materialPosition);
    this.currencySymbol = this.dataset.currencySymbol || '€';
    this.atcLabel = this.dataset.atcLabel || 'Ajouter au panier';

    this.buildMaterialPrices();

    this.cards = Array.from(this.querySelectorAll('.bbb-card'));
    this.atcButton = this.querySelector('.bbb-atc');
    this.errorMsg = this.querySelector('.bbb-error');
    this.stickyBar = this.querySelector('.bbb-sticky');
    this.stickyAtcButton = this.querySelector('.bbb-sticky__atc');
    this.materialSeg = this.querySelector('[data-material-seg]');
    this.colorSwatches = this.querySelector('[data-color-swatches]');
    this.colorNameEl = this.querySelector('[data-color-name]');

    // État "trousse unique" (haut de page)
    this.topMaterial = this.data.materials[0];
    this.topColor = this.availableColorsFor(this.topMaterial)[0]?.name || this.data.colors[0]?.name || '';

    this.renderMaterialSeg();
    this.renderTopSwatches();

    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.selectCard(card));
    });

    this.atcButton?.addEventListener('click', () => this.addToCart());
    this.stickyAtcButton?.addEventListener('click', () => this.addToCart());

    if (this.atcButton && this.stickyBar) {
      this.#stickyObserver = new IntersectionObserver(
        ([entry]) => {
          this.stickyBar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        },
        { threshold: 0 }
      );
      this.#stickyObserver.observe(this.atcButton);
    }

    const defaultCard = this.querySelector('.bbb-card.is-selected') || this.cards[1] || this.cards[0];
    if (defaultCard) this.selectCard(defaultCard, { init: true });

    // Filtre initial de la galerie sur la couleur par défaut (une fois la galerie rendue).
    this.applyInitialGalleryFilter();

    this.startCountdown();
  }

  #stickyObserver;
  #countdownTimer;
  #initFilterTimer;

  /**
   * Applique le filtre couleur au chargement.
   *
   * Volontairement sans requestAnimationFrame : dans un onglet ouvert en arrière-plan
   * (« ouvrir dans un nouvel onglet » depuis une pub / un lien social) le navigateur ne
   * déclenche aucune frame, et le filtre initial ne partait jamais → la galerie affichait
   * toutes les couleurs jusqu'au premier clic. On tente donc immédiatement, puis on
   * réessaie brièvement au cas où la galerie serait rendue après nous (éditeur de thème,
   * rechargement de section).
   */
  applyInitialGalleryFilter() {
    const attempt = () => {
      const gallery = document.querySelector('media-gallery[data-media-groups]');
      if (!gallery || !gallery.querySelector('slideshow-slide[data-media-id]')) return false;
      this.filterGalleryByColor(this.topMaterial, this.topColor);
      return true;
    };

    if (attempt()) return;

    let tries = 0;
    this.#initFilterTimer = setInterval(() => {
      if (attempt() || ++tries > 20) clearInterval(this.#initFilterTimer);
    }, 100);
  }

  disconnectedCallback() {
    this.#stickyObserver?.disconnect();
    clearInterval(this.#countdownTimer);
    clearInterval(this.#initFilterTimer);
  }

  /** Prix "de référence" par matière (couleurs = même prix) : 1re variante de la matière. */
  buildMaterialPrices() {
    this.materialPrice = {};
    this.materialRRP = {};
    this.data.materials.forEach((m) => {
      const v = this.data.variants.find((x) => x.options[this.materialPosition - 1] === m);
      if (v) {
        this.materialPrice[m] = v.price;
        this.materialRRP[m] = v.compareAtPrice || v.price;
      }
    });
  }

  findVariant(materialName, colorName) {
    return this.data.variants.find(
      (v) => v.options[this.materialPosition - 1] === materialName && v.options[this.colorPosition - 1] === colorName
    );
  }

  availableColorsFor(materialName) {
    return this.data.colors.filter((c) => this.findVariant(materialName, c.name));
  }

  /** Couleur d'affichage : swatch admin si défini, sinon fallback (couleurs exactes du mockup). */
  swatchColor(c) {
    return c.swatch || this.swatchFallback[c.name] || '#ccc';
  }

  hasAnyVariantFor(materialName) {
    return this.data.variants.some((v) => v.options[this.materialPosition - 1] === materialName);
  }

  fmt(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' ' + this.currencySymbol;
  }

  fmtRound(cents) {
    return Math.round(cents / 100) + ' ' + this.currencySymbol;
  }

  /* ---------- Sélecteur Matière / Couleur du haut ---------- */

  renderMaterialSeg() {
    if (!this.materialSeg) return;
    this.materialSeg.innerHTML = '';
    this.data.materials.forEach((m) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = this.materialPrice[m] ? `${m} · ${this.fmt(this.materialPrice[m])}` : m;
      btn.className = m === this.topMaterial ? 'is-active' : '';
      if (!this.hasAnyVariantFor(m)) btn.disabled = true;
      btn.addEventListener('click', () => {
        this.topMaterial = m;
        this.materialSeg.querySelectorAll('button').forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        const colors = this.availableColorsFor(m);
        if (!colors.find((c) => c.name === this.topColor)) this.topColor = colors[0]?.name || '';
        this.renderTopSwatches();
        this.syncGallery(this.topMaterial, this.topColor);
        this.updatePricing();
      });
      this.materialSeg.appendChild(btn);
    });
  }

  /** Met à jour le nom de la couleur + le badge « Épuisé » selon la disponibilité. */
  updateColorLabel() {
    if (this.colorNameEl) this.colorNameEl.textContent = this.topColor;
    const badge = this.querySelector('[data-color-soldout]');
    if (badge) {
      const v = this.findVariant(this.topMaterial, this.topColor);
      badge.hidden = !(v && !v.available);
    }
  }

  renderTopSwatches() {
    if (!this.colorSwatches) return;
    this.colorSwatches.innerHTML = '';
    const available = this.availableColorsFor(this.topMaterial);
    if (!available.find((c) => c.name === this.topColor)) this.topColor = available[0]?.name || '';
    this.updateColorLabel();

    available.forEach((c) => {
      const variant = this.findVariant(this.topMaterial, c.name);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className =
        'bbb-swatch' + (c.name === this.topColor ? ' is-active' : '') + (!variant?.available ? ' is-soldout' : '');
      btn.style.background = this.swatchColor(c);
      btn.title = variant?.available ? c.name : `${c.name} (épuisé)`;
      btn.setAttribute('aria-label', btn.title);
      btn.addEventListener('click', () => {
        this.topColor = c.name;
        this.colorSwatches.querySelectorAll('.bbb-swatch').forEach((s) => s.classList.remove('is-active'));
        btn.classList.add('is-active');
        this.updateColorLabel();
        this.syncGallery(this.topMaterial, this.topColor);
        this.updatePricing();
      });
      this.colorSwatches.appendChild(btn);
    });
  }

  /* ---------- Cartes bundle ---------- */

  selectCard(card, { init } = {}) {
    if (!init && card.classList.contains('is-selected')) return;
    this.cards.forEach((c) => {
      c.classList.remove('is-selected');
      const items = c.querySelector('.bbb-items');
      if (items) items.innerHTML = '';
    });
    card.classList.add('is-selected');
    this.renderItems(card, Number(card.dataset.qty));
    this.updatePricing();
  }

  /** Choix par trousse — uniquement pour 2/3 (la carte 1 utilise le sélecteur du haut). */
  renderItems(card, qty) {
    const wrap = card.querySelector('.bbb-items');
    if (!wrap) return;
    wrap.innerHTML = '';
    if (qty < 2) return;

    for (let i = 1; i <= qty; i++) {
      const row = document.createElement('div');
      row.className = 'bbb-item';
      row.dataset.material = this.topMaterial;
      row.dataset.color = this.availableColorsFor(this.topMaterial)[0]?.name || this.data.colors[0]?.name || '';

      const label = document.createElement('div');
      label.className = 'bbb-item__label';
      label.textContent = `Trousse ${i}`;

      const seg = document.createElement('div');
      seg.className = 'bbb-item__seg';
      this.data.materials.forEach((m) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = m;
        btn.className = m === row.dataset.material ? 'is-active' : '';
        if (!this.hasAnyVariantFor(m)) btn.disabled = true;
        btn.addEventListener('click', () => {
          seg.querySelectorAll('button').forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          row.dataset.material = m;
          const colors = this.availableColorsFor(m);
          if (!colors.find((c) => c.name === row.dataset.color)) row.dataset.color = colors[0]?.name || '';
          this.renderItemSwatches(row, i);
          if (i === 1) this.syncGallery(row.dataset.material, row.dataset.color);
          this.updatePricing();
        });
        seg.appendChild(btn);
      });

      const swatches = document.createElement('div');
      swatches.className = 'bbb-item__swatches';

      row.appendChild(label);
      row.appendChild(seg);
      row.appendChild(swatches);
      wrap.appendChild(row);

      this.renderItemSwatches(row, i);
    }
  }

  renderItemSwatches(row, i) {
    const wrap = row.querySelector('.bbb-item__swatches');
    if (!wrap) return;
    wrap.innerHTML = '';
    const available = this.availableColorsFor(row.dataset.material);
    if (!available.find((c) => c.name === row.dataset.color)) row.dataset.color = available[0]?.name || '';

    available.forEach((c) => {
      const variant = this.findVariant(row.dataset.material, c.name);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className =
        'bbb-swatch' + (c.name === row.dataset.color ? ' is-active' : '') + (!variant?.available ? ' is-soldout' : '');
      btn.style.background = this.swatchColor(c);
      btn.title = variant?.available ? c.name : `${c.name} (épuisé)`;
      btn.setAttribute('aria-label', btn.title);
      btn.addEventListener('click', () => {
        wrap.querySelectorAll('.bbb-swatch').forEach((s) => s.classList.remove('is-active'));
        btn.classList.add('is-active');
        row.dataset.color = c.name;
        if (i === 1) this.syncGallery(row.dataset.material, row.dataset.color);
        this.updatePricing();
      });
      wrap.appendChild(btn);
    });
  }

  get selectedCard() {
    return this.cards.find((c) => c.classList.contains('is-selected'));
  }

  /** Matières d'un bundle : les rows par trousse si présentes, sinon la matière du haut. */
  cardMaterials(card, qty) {
    const rows = Array.from(card.querySelectorAll('.bbb-item'));
    if (rows.length) return rows.map((r) => r.dataset.material);
    return Array(qty).fill(this.topMaterial);
  }

  selectedVariants() {
    const card = this.selectedCard;
    if (!card) return [];
    const rows = Array.from(card.querySelectorAll('.bbb-item'));
    if (rows.length) {
      return rows.map((r) => this.findVariant(r.dataset.material, r.dataset.color)).filter(Boolean);
    }
    const v = this.findVariant(this.topMaterial, this.topColor);
    return v ? [v] : [];
  }

  hasValidSelection() {
    const card = this.selectedCard;
    if (!card) return false;
    const qty = Number(card.dataset.qty);
    const variants = this.selectedVariants();
    return variants.length === qty && variants.every((v) => v.available);
  }

  computeBundle(qty, materials) {
    const sales = materials.map((m) => this.materialPrice[m] || 0);
    const was = materials.reduce((s, m) => s + (this.materialRRP[m] || 0), 0);
    let now;
    if (qty === 1) {
      now = sales[0];
    } else if (qty === 2) {
      // Remise pack de 2 (~5€) — comme la maquette, pour garder une échelle de prix cohérente.
      // ⚠️ Nécessite une remise automatique Shopify correspondante (sinon le panier facturera la somme pleine).
      now = sales[0] + sales[1] - 499;
    } else {
      const sorted = [...sales].sort((a, b) => a - b);
      const freeCount = Math.floor(qty / 3);
      const total = sorted.reduce((s, v) => s + v, 0);
      const freeSum = sorted.slice(0, freeCount).reduce((s, v) => s + v, 0);
      now = total - freeSum;
    }
    return { now, was, save: Math.max(0, was - now), per: Math.round(now / qty) };
  }

  updatePricing() {
    // Prix hero (trousse unique = matière du haut)
    const heroNow = this.querySelector('[data-hero-now]');
    const heroWas = this.querySelector('[data-hero-was]');
    const heroOff = this.querySelector('[data-hero-off]');
    const price = this.materialPrice[this.topMaterial] || 0;
    const rrp = this.materialRRP[this.topMaterial] || 0;
    if (heroNow) heroNow.textContent = this.fmt(price);
    if (heroWas) heroWas.textContent = rrp > price ? this.fmt(rrp) : '';
    if (heroOff) heroOff.textContent = rrp > price ? `Économisez ${this.fmtRound(rrp - price)}` : '';

    // Cartes bundle
    this.cards.forEach((card) => {
      const qty = Number(card.dataset.qty);
      const { now, was, save, per } = this.computeBundle(qty, this.cardMaterials(card, qty));
      const wasEl = card.querySelector('.bbb-card__was');
      const nowEl = card.querySelector('.bbb-card__now');
      const saveEl = card.querySelector('.bbb-card__save');
      const perEl = card.querySelector('.bbb-card__per-price');
      if (wasEl) wasEl.textContent = this.fmt(was);
      if (nowEl) nowEl.textContent = this.fmt(now);
      if (saveEl) {
        saveEl.textContent =
          qty === 1 && was > 0
            ? `Économisez ${Math.round((save / was) * 100)}%`
            : save > 0
              ? `Économisez ${this.fmtRound(save)}`
              : '';
      }
      if (perEl) perEl.textContent = this.fmt(per);
    });

    const selected = this.selectedCard;
    const now = selected?.querySelector('.bbb-card__now')?.textContent;
    const was = selected?.querySelector('.bbb-card__was')?.textContent;
    const hasSelection = this.hasValidSelection();

    if (this.atcButton) {
      this.atcButton.textContent = now ? `${this.atcLabel} — ${now}` : this.atcLabel;
      this.atcButton.disabled = !hasSelection;
    }
    if (this.stickyBar) {
      const stickyNow = this.stickyBar.querySelector('.bbb-sticky__now');
      const stickyWas = this.stickyBar.querySelector('.bbb-sticky__was');
      if (stickyNow) stickyNow.textContent = now || '';
      if (stickyWas) stickyWas.textContent = was || '';
    }
    if (this.stickyAtcButton) this.stickyAtcButton.disabled = !hasSelection;
  }

  /**
   * Fait suivre la galerie à la couleur choisie.
   *
   * La correspondance média→variante vient de `data-media-groups` (calculé en Liquid à
   * partir de l'association média↔variante de l'admin) : aucune dépendance aux noms de
   * fichiers, à l'ordre des images ou à la liste des variantes. Un groupe vaut soit 'g'
   * (global : vidéos, visuels génériques, alt contenant [all]) soit l'id de la variante.
   *
   * Le filtrage cible chaque élément portant un `data-media-id` — slides, vignettes,
   * grille et dialogue de zoom — plutôt que des positions : <slideshow-component>
   * déplace et clone des slides à l'exécution, un filtrage par index se décalerait.
   * Les médias masqués ne sont pas téléchargés (lazy-load) → la galerie reste légère.
   */
  filterGalleryByColor(materialName, colorName) {
    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    let groups;
    try {
      groups = JSON.parse(gallery.dataset.mediaGroups || '{}');
    } catch (e) {
      return; // carte illisible → on n'ose pas filtrer
    }

    const variant = this.findVariant(materialName, colorName);
    if (!variant) return;
    const wanted = String(variant.id);

    const isVisible = (group) => group === 'g' || group === wanted;
    // Filet de sécurité : on ne vide jamais la galerie.
    if (!Object.values(groups).some(isVisible)) return;

    // Uniquement les conteneurs de premier niveau : `.product-media` porte déjà un
    // data-media-id (thème) mais vit *à l'intérieur* d'un slide, rien à masquer là.
    gallery
      .querySelectorAll(
        [
          'slideshow-slide[data-media-id]',
          '.slideshow-controls__thumbnail[data-media-id]',
          '.media-gallery__grid > li[data-media-id]',
          '.dialog-thumbnails-list__thumbnail[data-media-id]',
          '.dialog-zoomed-gallery > li[data-media-id]',
        ].join(',')
      )
      .forEach((el) => {
        const group = groups[el.dataset.mediaId];
        if (group) el.style.display = isVisible(group) ? '' : 'none';
      });

    // Fait sauter la grande image sur la 1re photo de la couleur (API native du slideshow).
    // On ignore les slides sans data-media-id : le thème peut en insérer (gouttière,
    // clone) et elles ne correspondent à aucun média.
    const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
    const firstVisibleIndex = slides.findIndex(
      (slide) => slide.dataset.mediaId && slide.style.display !== 'none'
    );
    const slideshow = gallery.querySelector('slideshow-component');
    if (firstVisibleIndex >= 0 && slideshow && typeof slideshow.select === 'function') {
      try {
        slideshow.select(firstVisibleIndex);
      } catch (e) {
        /* no-op */
      }
    }
  }

  /** Alias conservé : la sélection de couleur pilote la galerie. */
  syncGallery(materialName, colorName) {
    this.filterGalleryByColor(materialName, colorName);
  }

  async addToCart() {
    if (!this.atcButton || !this.hasValidSelection()) return;
    const variants = this.selectedVariants();

    this.atcButton.disabled = true;
    if (this.stickyAtcButton) this.stickyAtcButton.disabled = true;
    if (this.errorMsg) this.errorMsg.hidden = true;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = Array.from(cartItemsComponents)
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    // Événement standard "add" (écouté par les pixels Shopify/Meta + le panier natif).
    // Dispatché sur l'élément (comme le product-form natif) → remonte jusqu'à document.
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: variants.map((v) => ({ merchandiseId: String(v.id), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    try {
      const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: variants.map((v) => ({ id: v.id, quantity: 1 })),
          sections: sectionIds.join(','),
        }),
      });
      const json = await response.json();

      if (json.status) {
        // Erreur Shopify (rupture, etc.) — vrai message, pas "erreur réseau".
        deferred.reject(new Error(json.message || 'Add to cart failed'));
        if (this.errorMsg) {
          this.errorMsg.textContent = json.message || 'Une erreur est survenue.';
          this.errorMsg.hidden = false;
        }
        return;
      }

      // Succès. On récupère le PANIER COMPLET (comme le flux natif via fetchCartData) — la réponse
      // de /cart/add.js avec `sections` ne contient PAS token/item_count/total_price, donc
      // createCartFromAjaxResponse échouait dessus (d'où la fausse "erreur réseau").
      const fullCart = await this.refreshCart();
      let cartForEvent = fullCart;
      try {
        cartForEvent = CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart);
      } catch (e) {
        cartForEvent = fullCart; // sécurité : on résout quand même pour ne jamais bloquer le tracking
      }
      deferred.resolve({
        cart: cartForEvent,
        detail: { items: json.items, source: 'bundle-buy-box', sections: json.sections, didError: false },
      });

      // Shopify Web Pixels: track add_to_cart pour chaque item du bundle
      publishAddedToCart(json.items);

      this.openCartDrawer();
    } catch (error) {
      deferred.reject(error);
      if (this.errorMsg) {
        this.errorMsg.textContent = 'Erreur réseau, veuillez réessayer.';
        this.errorMsg.hidden = false;
      }
    } finally {
      const hasSelection = this.hasValidSelection();
      this.atcButton.disabled = !hasSelection;
      if (this.stickyAtcButton) this.stickyAtcButton.disabled = !hasSelection;
    }
  }

  /** Rafraîchit le panier natif (morph de la section) et renvoie l'objet cart complet. */
  async refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent && typeof cartItemsComponent.fetchCartData === 'function') {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) =>
      r.json()
    );
  }

  /** Ouvre le tiroir panier natif (au cas où l'auto-open n'est pas activé). */
  openCartDrawer() {
    const themeDrawer = document.getElementById('cart-drawer');
    if (!themeDrawer) return;
    if (typeof themeDrawer.open === 'function') {
      themeDrawer.open();
    } else if (!themeDrawer.hasAttribute('open')) {
      themeDrawer.setAttribute('open', '');
    }
  }

  /** Compte à rebours "Commandez d'ici HH:MM:SS pour recevoir dès <date>" — tranches de 3h. */
  startCountdown() {
    const timerEls = this.querySelectorAll('.bbb-ctimer');
    const arrivalEls = this.querySelectorAll('.bbb-arrival');
    if (!timerEls.length) return;
    const pad = (n) => String(n).padStart(2, '0');
    const fmtDate = (d) => d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
    const tick = () => {
      const now = new Date();
      const nextH = (Math.floor(now.getHours() / 3) + 1) * 3;
      const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      cutoff.setHours(nextH);
      const diff = Math.max(0, cutoff - now);
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const sec = Math.floor((diff % 60000) / 1000);
      const txt = `${pad(h)}:${pad(m)}:${pad(sec)}`;
      timerEls.forEach((el) => (el.textContent = txt));
      const arrival = new Date();
      arrival.setDate(arrival.getDate() + 1);
      arrivalEls.forEach((el) => (el.textContent = fmtDate(arrival)));
    };
    tick();
    this.#countdownTimer = setInterval(tick, 1000);
  }
}

if (!customElements.get('bundle-buy-box')) {
  customElements.define('bundle-buy-box', BundleBuyBox);
}
